import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import I18n from '@/modules/i18n';
import AIBadge from '@/components/shared/AIBadge';
import showLevelingStatusToast from '@/components/shared/SectionAIGeneratingPanel/showLevelingStatusToast';
import { TCIDropdown } from '@/components/ui/Dropdown';
import Icon from '@/components/ui/Icon';
import { GradientSpinner } from '@/components/ui/Spinner';
import { TCITooltip } from '@/components/ui/Tooltip';
import { COLORS } from '@/components/ui/ui.constants';
import cn from '@/utils/cn';
import useFlags from '@/hooks/useFlags';
import useReadingLevelGeneratingStore from '@/stores/useReadingLevelGeneratingStore';
import CompareReadingLevelsModal from './CompareReadingLevelsModal';
import {
  AI_GRADE_BANDS,
  GRADE_BAND_LABELS,
  STANDARD_LEVEL,
  type GradeBand,
  type ReadingLevelOption,
} from './constants';

interface AIReadingLevelButtonProps {
  failedAILevel?: string | null;
  /** AI grade bands that already have generated content for this section. Reading page only. */
  availableGradeBands?: string[];
  classroomId?: number;
  fontSize?: string;
  /** Path to the generate_ai_reading_level endpoint. Reading page only. */
  generatePath?: string;
  inIframe?: boolean;
  isInDraggableToolbar?: boolean;
  isStaffer: boolean;
  locale?: string;
  onSelect?: (level: string) => void;
  /** AI grade bands this program may offer (Program#available_ai_reading_levels). */
  programGradeBands?: string[];
  programId?: number;
  readingLevel: string;
  readingLevelAEnabled?: boolean;
  sectionEndPage?: number;
  sectionId: number;
  sectionStartPage?: number;
  sectionTitle?: string;
  showAIReadingLevelsForSection?: boolean;
  studentCountsByLevel?: Record<string, number>;
  studentLeveledReading?: string | null;
}

interface BuildOptionsParams {
  availableGradeBands?: string[];
  isStaffer: boolean;
  programGradeBands?: string[];
  readingLevelAEnabled?: boolean;
  showAIReadingLevelsForSection?: boolean;
  studentLeveledReading?: string | null;
  subscriberAiReadingLevelsEnabled: boolean;
}

const buildOptions = ({
  availableGradeBands,
  isStaffer,
  programGradeBands,
  readingLevelAEnabled,
  showAIReadingLevelsForSection,
  studentLeveledReading,
  subscriberAiReadingLevelsEnabled,
}: BuildOptionsParams): ReadingLevelOption[] => {
  const standard: ReadingLevelOption = {
    isAI: false,
    label: I18n.t('standard'),
    value: STANDARD_LEVEL,
  };

  if (isStaffer) {
    const levelB: ReadingLevelOption = {
      isAI: false,
      label: I18n.t('reading_level_b'),
      value: 'b',
    };
    const hasLevelB = availableGradeBands?.includes('b');

    return [
      standard,
      ...(hasLevelB ? [levelB] : []),
      ...((subscriberAiReadingLevelsEnabled)
        ? AI_GRADE_BANDS
          .filter(band => (programGradeBands ?? []).includes(band))
          .map<ReadingLevelOption>(band => ({
          disabled: showAIReadingLevelsForSection === false,
          isAI: true,
          label: GRADE_BAND_LABELS[band],
          value: band,
        }))
        : []
      ),
    ];
  }

  const options: ReadingLevelOption[] = [];
  if (readingLevelAEnabled !== false) options.push(standard);
  if (studentLeveledReading) {
    const isAILevel = AI_GRADE_BANDS.includes(studentLeveledReading as GradeBand);
    options.push({
      disabled: isAILevel && showAIReadingLevelsForSection === false,
      isAI: false,
      label: I18n.t('leveled'),
      value: studentLeveledReading,
    });
  }
  return options;
};

const readingLevelFromUrl = (): string | null => (
  new URLSearchParams(window.location.search).get('reading_level')
);

interface ItemContentProps {
  isFailed?: boolean;
  isGenerating?: boolean;
  isRateLimited?: boolean;
  option: ReadingLevelOption;
  selected: boolean;
}

const ItemContent = ({
  isFailed,
  isGenerating: isItemGenerating,
  isRateLimited: isItemRateLimited,
  option,
  selected,
}: ItemContentProps) => {
  const content = (
    <span
      className={cn(
        'tw-flex tw-items-center tw-gap-1.5 tw-flex-1',
        option.disabled ? 'tw-text-gray-700' : '',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'tw-flex tw-items-center tw-justify-center tw-w-4 tw-shrink-0',
          isFailed ? 'tw-text-red' : 'tw-text-green',
        )}
      >
        {isFailed && <Icon color={COLORS.red} icon="Warning" size={16} />}
        {!isFailed && isItemGenerating && <GradientSpinner />}
        {!isFailed && !isItemGenerating && selected && <Icon color={COLORS.green} icon="Checkmark" size={16} />}
      </span>
      <span className="tw-flex-1">{option.label}</span>
      {isItemRateLimited && (
        <span className="tw-text-dark-grey tw-text-xs">{I18n.t('ai_daily_limit_reached_short')}</span>
      )}
      {!isItemRateLimited && isFailed && (
        <span className="tw-text-red tw-text-xs tw-font-semibold">{I18n.t('ai_reading_level_retry')}</span>
      )}
      {!isItemRateLimited && !isFailed && option.isAI && <AIBadge disabled={option.disabled} />}
    </span>
  );

  return content;
};

const AIReadingLevelButton = ({
  failedAILevel,
  availableGradeBands,
  classroomId,
  fontSize,
  generatePath,
  inIframe = false,
  isInDraggableToolbar,
  isStaffer,
  locale,
  onSelect,
  programGradeBands,
  programId,
  readingLevel,
  readingLevelAEnabled,
  sectionEndPage,
  sectionId,
  sectionStartPage,
  sectionTitle,
  showAIReadingLevelsForSection,
  studentCountsByLevel = {},
  studentLeveledReading = null,
}: AIReadingLevelButtonProps) => {
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { canToggleSubscriberAiLeveledReading, subscriberAiReadingLevelsEnabled } = useFlags();
  const {
    failed,
    generated,
    generating,
    rateLimited,
    setFailed,
    setRateLimited,
    startGenerating,
  } = useReadingLevelGeneratingStore();

  useEffect(() => {
    if (failedAILevel && generatePath) {
      setFailed({ generatePath, gradeBand: failedAILevel, sectionId });
    }
  }, []);

  useEffect(() => {
    if (rateLimited) return;

    axios
      .get(Routes.plato_api_ai_rate_limit_path())
      .then(({ data }) => {
        if (data.rate_limited) setRateLimited(true);
      })
      .catch(() => {
      });
  }, []);

  if (!canToggleSubscriberAiLeveledReading || !subscriberAiReadingLevelsEnabled) return null;
  if (!isStaffer && !studentLeveledReading && readingLevelAEnabled !== false) return null;

  const options = buildOptions({
    availableGradeBands,
    isStaffer,
    programGradeBands,
    readingLevelAEnabled,
    showAIReadingLevelsForSection,
    studentLeveledReading,
    subscriberAiReadingLevelsEnabled,
  });

  if (!isStaffer && options.length === 0) return null;

  const hasFailed = !onSelect && failed?.sectionId === sectionId;
  const isGenerating = !onSelect && generating?.sectionId === sectionId;
  const urlLevel = onSelect ? null : readingLevelFromUrl();
  const isAIBand = (value: string) => AI_GRADE_BANDS.includes(value as GradeBand);
  const shouldForceStandard = showAIReadingLevelsForSection === false
    && isAIBand(urlLevel ?? readingLevel);
  const selected = shouldForceStandard
    ? options.find(option => option.value === STANDARD_LEVEL) ?? options[0]
    : options.find(option => option.value === urlLevel)
      ?? options.find(option => option.value === readingLevel)
      ?? options[0];

  const hrefFor = (value: string) => Routes.shared_section_path(sectionId, {
    in_iframe: inIframe,
    program_id: programId,
    reading_level: value,
  });

  const needsInPlaceGeneration = (value: string) => {
    if (onSelect) return false;
    if (!isAIBand(value)) return false;

    return availableGradeBands !== undefined
      && !availableGradeBands.includes(value)
      && !generated.includes(`${sectionId}:${value}`)
      && !!generatePath;
  };

  const triggerGeneration = (gradeBand: string) => {
    startGenerating({ generatePath: generatePath!, gradeBand, programId, sectionId });
    showLevelingStatusToast(
      <Icon className="tw-shrink-0" color={COLORS.black} icon="InfoAlternate" size={24} />,
      I18n.t('ai_reading_level_preparing'),
      { autoClose: 3000 },
    );
  };

  const defaultIcon = (
    <Icon
      className="tw-mr-1"
      color={isInDraggableToolbar ? 'white' : COLORS['tci-purple']}
      icon="ReadingLevel"
    />
  );
  let triggerIcon;
  if (isGenerating && !dropdownOpen) {
    triggerIcon = <GradientSpinner />;
  } else if (hasFailed) {
    triggerIcon = <Icon className="tw-mr-1" color={COLORS.red} icon="Warning" size={16} />;
  } else {
    triggerIcon = defaultIcon;
  }

  return (
    <div className="tw-flex tw-items-center" data-walkme="ai-reading-level-selector">
      <TCITooltip open={tooltipOpen && !dropdownOpen} onOpenChange={setTooltipOpen}>
        <TCITooltip.Trigger asChild>
          <span>
            <TCIDropdown open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <TCIDropdown.Trigger asChild>
                <button
                  aria-label={I18n.t('select_reading_level')}
                  className={cn(
                    'text-action',
                    'tw-flex tw-items-center tw-gap-1.5 tw-h-10 tw-px-2.5',
                    'tw-bg-white tw-border tw-border-solid tw-rounded',
                    'tw-cursor-pointer hover:tw-shadow-md',
                    hasFailed ? 'tw-border-red tw-ring-1 tw-ring-red' : 'tw-border-med-grey',
                    { 'tw-px-2 !tw-h-8 hover:tw-bg-white/30': isInDraggableToolbar },
                  )}
                  type="button"
                >
                  {triggerIcon}
                  <i aria-hidden="true" className="fa fa-caret-down tw-text-tci-purple tw-mr-0" />
                </button>
              </TCIDropdown.Trigger>
              <TCIDropdown.Content align="start" side="bottom" width="unset">
                {options.map((option) => {
                  const isSelected = option.value === selected.value;
                  const ariaCurrent = isSelected ? 'true' : undefined;
                  const needsGeneration = needsInPlaceGeneration(option.value);
                  const isBandFailed = needsGeneration && hasFailed && failed?.gradeBand === option.value;
                  const isBandGenerating = needsGeneration && isGenerating && generating?.gradeBand === option.value;

                  if (option.disabled) {
                    return (
                      <TCITooltip key={option.value || option.label} side="right">
                        <TCITooltip.Trigger asChild>
                          <span>
                            <TCIDropdown.Item
                              aria-disabled
                              aria-current={ariaCurrent}
                              className="!tw-opacity-100 tw-cursor-not-allowed tw-pointer-events-none"
                            >
                              <ItemContent
                                isFailed={isBandFailed}
                                isGenerating={isBandGenerating}
                                option={option}
                                selected={isSelected}
                              />
                            </TCIDropdown.Item>
                          </span>
                        </TCITooltip.Trigger>
                        <TCITooltip.Content>
                          {isStaffer
                            ? I18n.t('ai_reading_level_disabled_for_section') : I18n.t('leveled_reading_disabled') }
                        </TCITooltip.Content>
                      </TCITooltip>
                    );
                  }

                  if (onSelect) {
                    return (
                      <TCIDropdown.Item
                        key={option.value || option.label}
                        aria-current={ariaCurrent}
                        onSelect={() => onSelect(option.value)}
                      >
                        <ItemContent
                          isFailed={isBandFailed}
                          isGenerating={isBandGenerating}
                          option={option}
                          selected={isSelected}
                        />
                      </TCIDropdown.Item>
                    );
                  }

                  if (needsGeneration) {
                    const isBandDisabled = ((rateLimited && isAIBand(option.value)) || option.disabled);
                    return (
                      <TCIDropdown.Item
                        key={option.value || option.label}
                        aria-current={ariaCurrent}
                        aria-disabled={isBandDisabled || undefined}
                        className={isBandDisabled ? 'tw-opacity-50 tw-cursor-not-allowed' : undefined}
                        onSelect={isBandDisabled ? undefined : () => triggerGeneration(option.value)}
                      >
                        <ItemContent
                          isFailed={isBandFailed}
                          isGenerating={isBandGenerating}
                          isRateLimited={isBandDisabled}
                          option={option}
                          selected={isSelected}
                        />
                      </TCIDropdown.Item>
                    );
                  }

                  return (
                    <TCIDropdown.Item key={option.value || option.label} asChild>
                      <a
                        aria-current={ariaCurrent}
                        className="!tw-no-underline"
                        href={hrefFor(option.value)}
                      >
                        <ItemContent option={option} selected={isSelected} />
                      </a>
                    </TCIDropdown.Item>
                  );
                })}
                {isStaffer && !onSelect && AI_GRADE_BANDS.includes(selected.value as GradeBand) && (
                  <>
                    <TCIDropdown.Separator />
                    <TCIDropdown.Item onSelect={() => setCompareModalOpen(true)}>
                      <span className="tw-flex tw-items-center tw-gap-1.5">
                        <Icon color={COLORS['tci-purple']} icon="Observe" size={16} />
                        <span>
                          {I18n.t('ai_compare_level', {
                            level: GRADE_BAND_LABELS[selected.value as GradeBand],
                          })}
                        </span>
                      </span>
                    </TCIDropdown.Item>
                  </>
                )}
              </TCIDropdown.Content>
            </TCIDropdown>
          </span>
        </TCITooltip.Trigger>
        <TCITooltip.Content>{I18n.t('reading_levels')}</TCITooltip.Content>
      </TCITooltip>
      {compareModalOpen && (
        <CompareReadingLevelsModal
          isOpen
          availableGradeBands={availableGradeBands ?? []}
          classroomId={classroomId}
          fontSize={fontSize}
          initialGradeBand={selected.value as GradeBand}
          locale={locale}
          programGradeBands={programGradeBands ?? []}
          programId={programId}
          sectionEndPage={sectionEndPage}
          sectionId={sectionId}
          sectionStartPage={sectionStartPage}
          sectionTitle={sectionTitle}
          studentCountsByLevel={studentCountsByLevel}
          onClose={() => setCompareModalOpen(false)}
        />
      )}
    </div>
  );
};

export default AIReadingLevelButton;
